// Mirrors PortalAPI's transaction DTOs (payments/TransactionDtos.cs). Money fields are integer cents.
// Enum-ish fields (cardIssuer, transactionType, transactionStatus, ...) are the proto enum names as strings,
// e.g. "Visa" / "AmericanExpress", "Sale" / "Void".

export interface TransactionSummary {
    id: string;
    locationName?: string | null;
    transactionDateTimeUtc: string;
    transactionDateTimeLocal: string;   // location wall-clock, bare ISO (no offset)
    locale: string;
    amount: number;
    tax: number;
    tip: number;
    invoiceNumber?: string | null;
    cardIssuer: string;
    cardNumber?: string | null;
    approvalCode?: string | null;
    transactionType: string;
    transactionStatus: string;
    isVoided: boolean;
    voidApprovalCode?: string | null;
}

export interface TransactionListResponse {
    items: TransactionSummary[];
    totalCount: number;
    page: number;
    pageSize: number;
}

export interface TransactionLineItem {
    id?: string | null;
    parentTransactionLineItemId?: string | null;
    transactionLineItemType: string;
    externalItemId?: string | null;
    name: string;
    quantity: number;
    unitPrice: number;
    amount: number;
    description?: string | null;
}

export interface TransactionDetail {
    id: string;
    organizationId: string;
    brandId: string;
    locationId: string;
    brandName?: string | null;
    locationName?: string | null;
    deviceId?: string | null;
    employeeId?: string | null;
    employeeName?: string | null;
    parentTransactionId?: string | null;
    transactionDateTimeUtc: string;
    transactionDateTimeLocal: string;   // location wall-clock, bare ISO (no offset)
    transactionType: string;
    transactionStatus: string;
    transactionResult: string;
    paymentType: string;
    digitalWalletType: string;
    invoiceNumber?: string | null;
    paymentGatewayTransactionId?: string | null;
    posTransactionId?: string | null;
    responseCode?: string | null;
    approvalCode?: string | null;
    registerNumber?: string | null;
    cardIssuer: string;
    cardNumber?: string | null;
    locale: string;
    timeZone: string;
    amount: number;
    tax: number;
    tip: number;
    description?: string | null;
    customerName?: string | null;
    customerEmail?: string | null;
    customerPhone?: string | null;
    signatureImageBase64?: string | null;
    isVoided: boolean;
    voidApprovalCode?: string | null;
    lineItems: TransactionLineItem[];
}

export interface TransactionReceipt {
    found: boolean;
    html: string;
}

export interface TransactionFilters {
    organizationId?: string;   // the org being viewed in the scope selector
    locationIds?: string[];    // multi-select; empty/undefined = all locations in the brand
    startDateTime?: string;   // ISO (UTC)
    endDateTime?: string;     // ISO (UTC)
    cardIssuer?: string;      // proto enum name, e.g. "Visa"
    transactionType?: string; // proto enum name, e.g. "Sale" / "Void"
    invoiceNumber?: string;
    page: number;
    pageSize: number;
}

// Filter dropdown options — the proto enum names the API binds/returns, paired with display labels.
export const CARD_ISSUERS: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'Visa', label: 'Visa' },
    { value: 'Mastercard', label: 'Mastercard' },
    { value: 'AmericanExpress', label: 'American Express' },
    { value: 'Discover', label: 'Discover' },
    { value: 'DinersClub', label: 'Diners Club' },
    { value: 'Jcb', label: 'JCB' },
    { value: 'GiftCard', label: 'Gift Card' },
];

export const TRANSACTION_TYPES: ReadonlyArray<{ value: string; label: string }> = [
    { value: 'Sale', label: 'Sale' },
    { value: 'Void', label: 'Void' },
];

export const cardIssuerLabel = (value: string): string =>
    CARD_ISSUERS.find(c => c.value === value)?.label ?? value;
